import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ClipboardList, Fuel, Truck, Wrench } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import { BadgeStatus } from '@/componentes/BadgeStatus';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { HistoriaNaMesa } from '@/historias/mesa';
import { STATUS_EQUIPAMENTO, ehStatusEquipamento } from '@/lib/dominio';
import { formatarData, formatarDataHora, formatarDecimal, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import type { EstadoDoVencimento, Gatilho } from '@/modulos/manutencao/api';
import { corDoEstado, ROTULO_DO_ESTADO, textoDoFaltam } from '@/modulos/manutencao/vencimento';
import type { Etapa } from '@/modulos/oficina/api';
import { numeroDaOrdem, ROTULO_DA_ETAPA } from '@/modulos/oficina/ordem';
import { rotuloDoTipo } from '@/modulos/operacao/conciliacao';

import { api, type Bloco } from './api';
import { ANCORA_DOS_REGISTROS, rolarParaOsRegistros, useBannerDeApontamento } from './banner';
import { contagem, diasDesde, excecoesDeCombustivel, haQuanto, maquinasIndisponiveis, textoDeAtualizacao } from './painel';
import { ResumoDaIa } from './ResumoDaIa';

/**
 * O painel de exceções — passo 9 do ciclo 1, história `DASH 01`.
 *
 * ## Uma pergunta por seção, e a data de cada resposta
 *
 * O v6 pede, na ferramenta 3 do módulo 11, "indisponibilidade, OS,
 * preventivas, divergências e registros faltantes, com atualização
 * identificada". Cada seção responde uma dessas, lendo o módulo de origem, e
 * diz quando foi o último registro que a sustenta — o risco escrito é "tratar
 * um painel desatualizado como visão em tempo real".
 *
 * Seção sem a permissão do módulo diz isso, em vez de sumir: sumir se leria
 * como "não há exceção".
 *
 * ## O que ele ainda não mostra
 *
 * O resultado (módulo 21), que depende da receita. O custo direto está em
 * `/custos`, e o resumo da IA (`IA 04`) entrou em 23/09, em `ResumoDaIa`.
 */

const ehEstado = (e: string): e is EstadoDoVencimento => Object.hasOwn(ROTULO_DO_ESTADO, e);
const ehGatilho = (g: string): g is Gatilho => g === 'horas' || g === 'km' || g === 'dias';
const ehEtapa = (e: string): e is Etapa => Object.hasOwn(ROTULO_DA_ETAPA, e);

const SUBTITULO = 'O que pede atenção hoje, com a data de cada informação.';

/* O espaço entre as seções é o mesmo da `.pagina` (R01, p.28): 24 px, e 16
   abaixo de 1024. Com `gap-6` fixo, a grade ficava com 24 no celular enquanto
   o resto da página já tinha passado para 16. */
const GRADE_DAS_SECOES =
  'grid grid-cols-2 gap-[var(--grade-espaco)] max-[1280px]:grid-cols-1 max-[1024px]:gap-[var(--grade-espaco-estreito)]';

function Secao<T>({
  id,
  titulo,
  bloco,
  modulo,
  para,
  rotuloDoLink,
  children,
}: {
  /** A âncora, quando um banner leva a esta seção ("Ver máquinas"). */
  readonly id?: string;
  readonly titulo: string;
  readonly bloco: Bloco<T>;
  readonly modulo: string;
  readonly para: '/equipamentos' | '/manutencao' | '/ordens' | '/estoque' | '/combustivel' | '/operacao' | '/operacao/conciliacao';
  readonly rotuloDoLink: string;
  readonly children: (aberto: Extract<Bloco<T>, { permitido: true }>) => ReactNode;
}) {
  return (
    <section {...(id ? { id, tabIndex: -1 } : {})} className="cartao flex scroll-mt-6 flex-col gap-3 p-6">
      {/* O carimbo é a promessa da tela ("a data de cada informação") e sai
          inteiro: quando não cabe ao lado do título, desce para baixo dele em
          vez de partir no meio da frase (R01, p.28). */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="txt-secao min-w-0 text-pretty text-tinta">{titulo}</h2>
        {bloco.permitido ? (
          <span className="txt-apoio whitespace-nowrap text-tinta-secundaria">
            {textoDeAtualizacao(bloco.atualizadoEm)}
          </span>
        ) : null}
      </div>
      {bloco.permitido ? (
        <>
          {children(bloco)}
          <div>
            <Link to={para} className="botao botao-terciario">
              {rotuloDoLink}
            </Link>
          </div>
        </>
      ) : (
        <p className="txt-apoio text-tinta-secundaria">
          {`Seu perfil não vê o módulo de ${modulo}. Peça a liberação ao administrador da empresa.`}
        </p>
      )}
    </section>
  );
}

function Linha({
  principal,
  apoio,
  valor,
  unidade,
  alerta = false,
  corDoApoio = 'text-tinta-secundaria',
}: {
  readonly principal: ReactNode;
  readonly apoio: ReactNode;
  /** Já formatado. A unidade vem à parte: sai em cinza e sem negrito (p.12). */
  readonly valor?: string;
  readonly unidade?: string;
  /**
   * Contagem de exceção maior que zero: o próprio número é o alerta e sai em
   * vermelho (R02, p.31 e p.32). Zero fica preto. Só para contagem, sem unidade.
   */
  readonly alerta?: boolean;
  /** R02: vencida em vermelho, próxima em âmbar. */
  readonly corDoApoio?: string;
}) {
  return (
    <li className="flex items-start justify-between gap-4 border-b border-borda-sutil pb-2 last:border-b-0">
      <span className="flex min-w-0 flex-col">
        <span className="txt-corpo text-tinta">{principal}</span>
        <span className={`txt-apoio ${corDoApoio}`}>{apoio}</span>
      </span>
      {valor === undefined ? null : (
        /* Número e unidade não se separam: "3 d" partido em duas linhas se lê
           como dois dados. */
        <span className="shrink-0 whitespace-nowrap">
          {unidade ? (
            <Medida valor={valor} unidade={unidade} />
          ) : (
            <span className={`numero ${alerta ? 'text-parada' : 'text-tinta'}`}>{valor}</span>
          )}
        </span>
      )}
    </li>
  );
}

/** Código da máquina em monoespaçada, para comparar na vertical (p.18). */
function Codigo({ children }: { readonly children: string }) {
  return <span className="codigo">{children}</span>;
}

/** Código e selo de status (R02, regra 12): o status nunca vai como texto corrido. */
function MaquinaComStatus({
  codigo,
  status,
  depois,
}: {
  readonly codigo: string;
  readonly status: string;
  readonly depois?: string;
}) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <Codigo>{codigo}</Codigo>
      {ehStatusEquipamento(status) ? <BadgeStatus status={status} /> : <span>{status}</span>}
      {depois ? <span>{depois}</span> : null}
    </span>
  );
}

function Nada({ texto }: { readonly texto: string }) {
  return <p className="txt-apoio text-tinta-secundaria">{texto}</p>;
}

/** As seis seções, na ordem da tela: o título não depende da resposta. */
const SECOES_DO_PAINEL = [
  'Indisponibilidade',
  'Ordens de serviço',
  'Preventivas',
  'Estoque abaixo do mínimo',
  'Divergências de combustível',
  'Registros faltantes',
] as const;

/**
 * Carregando (p.32): a forma do painel, no lugar exato de cada parte, para o
 * layout não pular quando o dado chega. O que já se sabe aparece — o rótulo e o
 * ícone de cada indicador, o título de cada seção —, e só o dado é esqueleto:
 * o número do card, o carimbo de atualização e as linhas de cada seção, na
 * forma da `Linha` (duas linhas à esquerda, o número à direita).
 */
function EsqueletoDoPainel() {
  return (
    <>
      <FaixaIndicadores>
        <CardIndicador carregando rotulo="Máquinas indisponíveis" icone={Truck} tom="parada" valor="" contexto="" />
        <CardIndicador carregando rotulo="Preventivas vencidas" icone={Wrench} tom="parada" valor="" contexto="" />
        <CardIndicador carregando rotulo="O.S. abertas" icone={ClipboardList} tom="manutencao" valor="" contexto="" />
        <CardIndicador carregando rotulo="Exceções de combustível" icone={Fuel} tom="marca" valor="" contexto="" />
      </FaixaIndicadores>
      <ResumoDaIa />
      <div className={GRADE_DAS_SECOES}>
        {SECOES_DO_PAINEL.map((titulo) => (
          <section key={titulo} className="cartao flex flex-col gap-3 p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h2 className="txt-secao min-w-0 text-pretty text-tinta">{titulo}</h2>
              <Barra className="h-3 w-32" />
            </div>
            <ul aria-hidden="true" className="flex flex-col gap-2">
              {[0, 1, 2].map((i) => (
                <li key={i} className="flex items-start justify-between gap-4 border-b border-borda-sutil pb-2 last:border-b-0">
                  <span className="flex min-w-0 flex-1 flex-col gap-2 py-1">
                    <Barra className={`h-3 ${i === 1 ? 'w-2/5' : 'w-1/2'}`} />
                    <Barra className={`h-3 ${i === 2 ? 'w-3/5' : 'w-3/4'}`} />
                  </span>
                  <Barra className="mt-1 h-3 w-8" />
                </li>
              ))}
            </ul>
            <Barra className="h-[var(--controle-m)] w-32 rounded-controle" />
          </section>
        ))}
      </div>
    </>
  );
}

export function PaginaPainel() {
  const navegar = useNavigate();
  const painel = useQuery({ queryKey: ['painel'], queryFn: () => api.painel(), refetchInterval: 5 * 60_000 });
  /* O banner da p.33: máquinas operando sem turno apontado. */
  useBannerDeApontamento();
  /* Quem chega por "Ver máquinas" de outra tela chega antes do painel: a
     seção só existe depois da resposta, e é aí que a tela rola até ela. */
  const carregado = painel.data !== undefined;
  useEffect(() => {
    if (carregado && location.hash === `#${ANCORA_DOS_REGISTROS}`) rolarParaOsRegistros();
  }, [carregado]);

  if (painel.isPending) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina" aria-busy="true">
          <CabecalhoPagina sobretitulo="Painel" titulo="Situação da frota e exceções" subtitulo={SUBTITULO} />
          <span role="status" className="sr-only">
            Carregando o painel
          </span>
          <EsqueletoDoPainel />
        </div>
      </div>
    );
  }
  if (painel.isError) {
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina sobretitulo="Painel" titulo="Situação da frota e exceções" subtitulo={SUBTITULO} />
          <EstadoVazio
            titulo="Não foi possível carregar o painel"
            descricao={explicarFalha(painel.error).comoResolver}
            acao={
              <Botao nivel="secundario" onClick={() => void painel.refetch()}>
                Carregar de novo
              </Botao>
            }
          />
        </div>
      </div>
    );
  }

  const p = painel.data;
  /* Da distribuição por status, e não do tamanho da lista: a lista vem
     cortada nas 10 mais antigas, e o card travava em 10. */
  const indisponiveis = p.frota.permitido ? maquinasIndisponiveis(p.frota.porStatus) : null;
  const frotaTotal = p.frota.permitido ? p.frota.porStatus.reduce((soma, s) => soma + s.maquinas, 0) : 0;
  const vencidas = p.preventivas.permitido ? p.preventivas.vencidas : null;
  const combustivel = p.combustivel.permitido ? excecoesDeCombustivel(p.combustivel) : null;
  const valor = (n: number | null) => (n === null ? '—' : formatarInteiro(n));

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Painel"
          titulo="Situação da frota e exceções"
          subtitulo={`${SUBTITULO} Montado ${haQuanto(p.geradoEm)}.`}
        />

        <FaixaIndicadores>
          <CardIndicador
            historia="IND-PNL-01"
            rotulo="Máquinas indisponíveis"
            icone={Truck}
            tom="parada"
            /* R02: máquina parada é o próprio alerta. Zero sai em preto. */
            alerta={(indisponiveis ?? 0) > 0}
            valor={valor(indisponiveis)}
            /* Contexto é a base (p.17), curto para a faixa se ler de relance.
               O que conta como indisponível — parada ou em manutenção — está
               na seção Indisponibilidade, logo abaixo. */
            contexto={
              indisponiveis === null ? 'sem acesso à frota' : `de ${contagem(frotaTotal, 'máquina', 'máquinas')}`
            }
            {...(indisponiveis !== null && frotaTotal > 0 ? { proporcao: indisponiveis / frotaTotal } : {})}
            aoAbrir={() => void navegar({ to: '/equipamentos', search: {} })}
          />
          <CardIndicador
            historia="IND-PNL-02"
            rotulo="Preventivas vencidas"
            icone={Wrench}
            /* O exemplo da R02 (p.31) desenha "Manutenção vencida" inteiro no
               vermelho: bloco, ícone e número. Vencida é alerta, não assunto. */
            tom="parada"
            alerta={(vencidas ?? 0) > 0}
            valor={valor(vencidas)}
            contexto={
              p.preventivas.permitido
                ? contagem(p.preventivas.venceLogo, 'vence logo', 'vencem logo')
                : 'sem acesso à manutenção'
            }
            aoAbrir={() => void navegar({ to: '/manutencao' })}
          />
          <CardIndicador
            historia="IND-PNL-03"
            rotulo="O.S. abertas"
            icone={ClipboardList}
            tom="manutencao"
            valor={valor(p.ordens.permitido ? p.ordens.abertas : null)}
            contexto={
              p.ordens.permitido
                ? `${formatarInteiro(p.ordens.aguardandoPeca)} aguardando peça`
                : 'sem acesso à oficina'
            }
            aoAbrir={() => void navegar({ to: '/ordens' })}
          />
          <CardIndicador
            historia="IND-PNL-04"
            rotulo="Exceções de combustível"
            icone={Fuel}
            tom="marca"
            /* Divergência aberta é o próprio alerta (R02). */
            alerta={(combustivel ?? 0) > 0}
            valor={valor(combustivel)}
            /* Contexto é a base da soma (p.17). O que cada parcela quer dizer
               está na seção "Divergências de combustível", logo abaixo. */
            contexto={combustivel === null ? 'sem acesso ao combustível' : 'em 4 verificações'}
            aoAbrir={() => void navegar({ to: '/combustivel' })}
          />
        </FaixaIndicadores>

        {/* IA 04: some sozinho para quem não pode perguntar ao assistente. */}
        <ResumoDaIa />

        <div className={GRADE_DAS_SECOES}>
          <Secao titulo="Indisponibilidade" bloco={p.frota} modulo="equipamentos" para="/equipamentos" rotuloDoLink="Abrir frota">
            {(f) => (
              <>
                {/* A DISTRIBUIÇÃO POR STATUS é tabela, e não gráfico: seis
                    números lidos por nome, cada um com o rótulo escrito. A
                    linha tem a altura fixa da tabela (p.18), e não o que o
                    selo e o respiro somarem. */}
                <table className="w-full">
                  <tbody>
                    {[...STATUS_EQUIPAMENTO, 'Sem situação'].map((status) => {
                      const n = f.porStatus.find((s) => s.status === status)?.maquinas ?? 0;
                      return (
                        <tr key={status} className="h-[var(--linha-compacta)] border-b border-borda-sutil last:border-b-0">
                          <td>
                            {ehStatusEquipamento(status) ? (
                              <BadgeStatus status={status} />
                            ) : (
                              <span className="txt-apoio text-tinta-secundaria">{status}</span>
                            )}
                          </td>
                          <td className="numero text-right txt-corpo text-tinta">{formatarInteiro(n)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* "Em manutenção" é a máquina na oficina, com O.S. aberta
                    (p.23). Sem a O.S., o painel mostraria "Em manutenção 1" ao
                    lado de "Nenhuma O.S. aberta" sem dizer que as duas não
                    batem: a exceção vai escrita, com o que fazer. */}
                {f.emManutencaoSemOrdem !== null && f.emManutencaoSemOrdem > 0 ? (
                  <ul className="flex flex-col gap-2">
                    <Linha
                      principal={
                        <span className="flex flex-wrap items-center gap-2">
                          <BadgeStatus status="Em manutenção" />
                          <span>sem O.S. aberta</span>
                        </span>
                      }
                      apoio="abra a O.S. na oficina ou corrija a situação da máquina"
                      valor={formatarInteiro(f.emManutencaoSemOrdem)}
                      alerta
                    />
                  </ul>
                ) : null}
                {f.indisponiveis.length === 0 ? (
                  <Nada texto="Nenhuma máquina parada ou em manutenção agora." />
                ) : (
                  <ul className="flex flex-col gap-2">
                    {f.indisponiveis.map((m) => (
                      <Linha
                        key={m.equipamentoId}
                        principal={
                          <MaquinaComStatus
                            codigo={m.codigo}
                            status={m.status}
                            {...(m.semOrdemAberta === true ? { depois: 'sem O.S. aberta' } : {})}
                          />
                        }
                        apoio={
                          <>
                            {`${m.motivo} · desde `}
                            {/* Data e hora não se separam: "· 19:01" sozinho na
                                linha de baixo se lê como outro dado. */}
                            <span className="whitespace-nowrap">{formatarDataHora(new Date(m.desde))}</span>
                          </>
                        }
                        valor={formatarInteiro(diasDesde(m.desde))}
                        unidade="d"
                      />
                    ))}
                  </ul>
                )}
              </>
            )}
          </Secao>

          <Secao titulo="Ordens de serviço" bloco={p.ordens} modulo="oficina" para="/ordens" rotuloDoLink="Abrir oficina">
            {(o) =>
              o.antigas.length === 0 ? (
                <Nada texto="Nenhuma O.S. aberta." />
              ) : (
                <>
                  <p className="txt-apoio text-tinta-secundaria">
                    {`${contagem(o.maquinasParadas, 'máquina parada', 'máquinas paradas')} por O.S. · ${contagem(o.paraEncerrar, 'liberada', 'liberadas')} sem encerrar`}
                  </p>
                  <ul className="flex flex-col gap-2">
                    {o.antigas.map((os) => (
                      <Linha
                        key={os.id}
                        principal={
                          <>
                            {`${numeroDaOrdem(os.numero)} · `}
                            <Codigo>{os.equipamento}</Codigo>
                          </>
                        }
                        apoio={`${ehEtapa(os.etapa) ? ROTULO_DA_ETAPA[os.etapa] : os.etapa}${os.etapaDesde ? ` ${haQuanto(os.etapaDesde)}` : ''} · ${os.sintoma}`}
                        valor={formatarInteiro(diasDesde(os.abertaEm))}
                        unidade="d"
                      />
                    ))}
                  </ul>
                </>
              )
            }
          </Secao>

          <Secao titulo="Preventivas" bloco={p.preventivas} modulo="manutenção" para="/manutencao" rotuloDoLink="Abrir manutenção">
            {(m) => (
              <>
                <p className="txt-apoio text-tinta-secundaria">
                  {`${contagem(m.semPontoDePartida, 'tarefa', 'tarefas')} sem ponto de partida · ${formatarInteiro(m.semLeitura)} sem leitura de medidor`}
                </p>
                {m.primeiras.length === 0 ? (
                  <Nada texto="Nenhuma preventiva vencida ou vencendo." />
                ) : (
                  <ul className="flex flex-col gap-2">
                    {m.primeiras.map((v) => (
                      <Linha
                        key={`${v.equipamentoId}-${v.descricao}`}
                        principal={
                          <>
                            <Codigo>{v.equipamento}</Codigo>
                            {` · ${v.descricao}`}
                          </>
                        }
                        corDoApoio={corDoEstado(v.estado) ?? 'text-tinta-secundaria'}
                        apoio={[
                          ehEstado(v.estado) ? ROTULO_DO_ESTADO[v.estado] : v.estado,
                          ehGatilho(v.gatilho) ? textoDoFaltam({ faltam: v.faltam, gatilho: v.gatilho }) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    ))}
                  </ul>
                )}
              </>
            )}
          </Secao>

          <Secao titulo="Estoque abaixo do mínimo" bloco={p.estoque} modulo="estoque" para="/estoque" rotuloDoLink="Abrir estoque">
            {(e) =>
              e.abaixo.length === 0 ? (
                <Nada
                  texto={
                    e.faltaParaPreventivas > 0
                      ? `Nenhum item abaixo do mínimo, mas ${contagem(e.faltaParaPreventivas, 'item vai', 'itens vão')} faltar para as preventivas vencidas ou vencendo.`
                      : 'Nenhum item abaixo do mínimo.'
                  }
                />
              ) : (
                <>
                  {/* As contagens vão em linha, como nas outras seções, para o
                      número de alerta sair em vermelho (R02) em vez de se
                      perder no meio de uma frase. */}
                  <ul className="flex flex-col gap-2">
                    <Linha
                      principal="Posições abaixo do mínimo"
                      apoio={`${formatarInteiro(e.criticosAbaixo)} de item crítico`}
                      valor={formatarInteiro(e.abaixoDoMinimo)}
                      alerta={e.abaixoDoMinimo > 0}
                    />
                    {e.faltaParaPreventivas > 0 ? (
                      <Linha
                        principal="Itens que vão faltar"
                        apoio="para as preventivas vencidas ou vencendo"
                        valor={formatarInteiro(e.faltaParaPreventivas)}
                        alerta
                      />
                    ) : null}
                  </ul>
                  <ul className="flex flex-col gap-2 border-t border-borda-sutil pt-2">
                    {e.abaixo.map((s) => (
                      <Linha
                        key={`${s.produtoId}-${s.deposito}`}
                        principal={`${s.produto}${s.critico ? ' · crítico' : ''}`}
                        apoio={
                          <>
                            {/* Código em monoespaçada, como o da máquina (p.18). */}
                            <span className="mono txt-apoio whitespace-nowrap">{s.produtoCodigo}</span>
                            {` · ${s.deposito} · mínimo `}
                            <span className="whitespace-nowrap">{`${formatarDecimal(s.estoqueMinimo, 1)} ${s.unidade}`}</span>
                          </>
                        }
                        valor={formatarDecimal(s.disponivel, 1)}
                        unidade={s.unidade}
                      />
                    ))}
                  </ul>
                </>
              )
            }
          </Secao>

          <Secao titulo="Divergências de combustível" bloco={p.combustivel} modulo="combustível" para="/combustivel" rotuloDoLink="Abrir combustível">
            {(c) => (
              <>
                <ul className="flex flex-col gap-2">
                  <Linha
                    principal="Consumo fora da faixa"
                    apoio="últimos 7 dias"
                    valor={formatarInteiro(c.foraDaFaixa7Dias)}
                    alerta={c.foraDaFaixa7Dias > 0}
                  />
                  <Linha
                    principal="Cobranças divergentes"
                    apoio={contagem(c.cobrancasAbertas, 'ainda aberta', 'ainda abertas')}
                    valor={formatarInteiro(c.cobrancasDivergentes)}
                    alerta={c.cobrancasDivergentes > 0}
                  />
                  <Linha
                    principal="Tanques acima da capacidade"
                    apoio="volume lançado maior que o tanque: corrija o lançamento errado"
                    valor={formatarInteiro(c.tanquesAcimaDaCapacidade)}
                    alerta={c.tanquesAcimaDaCapacidade > 0}
                  />
                  <Linha
                    principal="Ajustes esperando aprovação"
                    apoio="correções e balanços que pedem quem tem alçada de ajuste"
                    valor={formatarInteiro(c.ajustesEsperando)}
                    alerta={c.ajustesEsperando > 0}
                  />
                  <Linha
                    principal="Abastecimentos sem operador"
                    apoio="últimos 30 dias, lançamento incompleto"
                    valor={formatarInteiro(c.semOperador30Dias)}
                    alerta={c.semOperador30Dias > 0}
                  />
                </ul>
                {c.foraDaFaixa.length === 0 ? null : (
                  <ul className="flex flex-col gap-2 border-t border-borda-sutil pt-2">
                    {c.foraDaFaixa.map((a) => (
                      <Linha
                        key={a.abastecimentoId}
                        principal={
                          <>
                            <Codigo>{a.equipamento}</Codigo>
                            {` · ${a.produto}`}
                          </>
                        }
                        apoio={
                          <>
                            {`faixa ${a.minimo === null ? '—' : formatarDecimal(a.minimo, 1)} a ${a.maximo === null ? '—' : formatarDecimal(a.maximo, 1)} · `}
                            <span className="whitespace-nowrap">{formatarDataHora(new Date(a.ocorridoEm))}</span>
                          </>
                        }
                        valor={formatarDecimal(a.consumo, 1)}
                        unidade={a.tipoMedidor === 'odometro' ? 'L/km' : 'L/h'}
                      />
                    ))}
                  </ul>
                )}
              </>
            )}
          </Secao>

          <Secao id={ANCORA_DOS_REGISTROS} titulo="Registros faltantes" bloco={p.registros} modulo="operação" para="/operacao" rotuloDoLink="Abrir operação">
            {(r) => (
              <>
                <ul className="flex flex-col gap-2">
                  <Linha
                    principal="Turnos sem conferência"
                    apoio={
                      r.naoConferidoMaisAntigo
                        ? `o mais antigo há ${contagem(diasDesde(r.naoConferidoMaisAntigo), 'dia', 'dias')}`
                        : 'nenhum pendente'
                    }
                    valor={formatarInteiro(r.naoConferidos)}
                    alerta={r.naoConferidos > 0}
                  />
                </ul>
                <HistoriaNaMesa codigo="NUM-PNL-10">
                  {r.operandoSemApontamento.length === 0 ? (
                    <Nada texto="Toda máquina operando tem turno apontado nos últimos dois dias." />
                  ) : (
                    <ul className="flex flex-col gap-2 border-t border-borda-sutil pt-2">
                      {/* A situação diz que trabalha, e ninguém fechou turno dela:
                          uma das duas está errada, e o painel não sabe qual. */}
                      {r.operandoSemApontamento.map((m) => (
                        <Linha
                          key={m.equipamentoId}
                          principal={<MaquinaComStatus codigo={m.codigo} status="Operando" depois="sem apontamento" />}
                          apoio={
                            m.ultimoApontamento
                              ? `último turno apontado há ${contagem(diasDesde(m.ultimoApontamento), 'dia', 'dias')}`
                              : 'nenhum turno apontado'
                          }
                        />
                      ))}
                    </ul>
                  )}
                </HistoriaNaMesa>
              </>
            )}
          </Secao>

          {/* §9.2 do guia: "divergências de apropriação acima da tolerância".
              Existem desde a OPE 03; o servidor de antes dela não manda o
              bloco, e aí a seção não aparece. */}
          {p.apropriacao ? (
            <Secao
              titulo="Divergências de apropriação"
              bloco={p.apropriacao}
              modulo="operação"
              para="/operacao/conciliacao"
              rotuloDoLink="Abrir conciliação"
            >
              {(d) =>
                d.abertas === 0 ? (
                  <Nada texto="Nenhuma divergência de horas aberta acima da tolerância." />
                ) : (
                  <>
                    <ul className="flex flex-col gap-2">
                      <Linha
                        principal="Divergências abertas"
                        apoio={`${formatarDecimal(d.horasAExplicar, 1)} h a explicar`}
                        valor={formatarInteiro(d.abertas)}
                        alerta={d.abertas > 0}
                      />
                      <Linha
                        principal="Sem responsável"
                        apoio="ninguém foi escolhido para tratar"
                        valor={formatarInteiro(d.semResponsavel)}
                        alerta={d.semResponsavel > 0}
                      />
                    </ul>
                    <ul className="flex flex-col gap-2 border-t border-borda-sutil pt-2">
                      {d.primeiras.map((x) => (
                        <Linha
                          key={x.id}
                          principal={
                            <>
                              <Codigo>{x.equipamento}</Codigo>
                              {` · ${rotuloDoTipo(x.tipo)}`}
                            </>
                          }
                          apoio={formatarData(new Date(`${x.dia}T12:00:00`))}
                          valor={formatarDecimal(x.diferencaH, 2)}
                          unidade="h"
                        />
                      ))}
                    </ul>
                  </>
                )
              }
            </Secao>
          ) : null}
        </div>

        <p className="txt-apoio text-tinta-secundaria">
          O custo e a fila de prioridades por máquina estão na Visão do gestor. O resultado por máquina e por contrato
          ainda não aparece: depende da receita, que entra com contratos e medições.
        </p>
      </div>
    </div>
  );
}
